/**
 * BookingConfirmation.jsx  —  Route: /confirmation/:id
 * Modern · Animated · Fully Responsive
 */

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

// const API_BASE = "http://localhost:5000/api";
const API_BASE = "http://192.168.0.57:5000/api";
const n  = v => Number(v) || 0;
const fmtPrice = v => "₹" + Math.round(n(v)).toLocaleString("en-IN");
const fmtDate  = d => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }); }
  catch { return String(d); }
};

// ─── CONFETTI ────────────────────────────────────────────────
function Confetti({ active }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const COLORS = ["#C9A84C","#E8C97A","#1D9E75","#ffffff","#F59E0B","#6EE7B7"];
    const pieces = Array.from({ length: 120 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * -canvas.height,
      w:  Math.random() * 10 + 4,
      h:  Math.random() * 5  + 3,
      r:  Math.random() * Math.PI * 2,
      vx: (Math.random() - .5) * 2,
      vy: Math.random() * 4 + 2,
      vr: (Math.random() - .5) * .15,
      c:  COLORS[Math.floor(Math.random() * COLORS.length)],
      o:  1,
    }));

    let raf, frame = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.r += p.vr;
        if (frame > 120) p.o = Math.max(0, p.o - .012);
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.globalAlpha = p.o;
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
        ctx.restore();
      });
      if (pieces.some(p => p.o > 0)) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return <canvas ref={canvasRef} style={{
    position:"fixed", inset:0, pointerEvents:"none", zIndex:9999,
  }} />;
}

// ─── ANIMATED NUMBER ─────────────────────────────────────────
function AnimatedPrice({ value, duration = 1200 }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    const target = Math.round(n(value));
    const start  = performance.now();
    const tick   = now => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplayed(Math.round(ease * target));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value]);
  return <>"₹" + displayed.toLocaleString("en-IN")</>;
}

// ─── CSS ─────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@300;400;500&display=swap');

:root {
  --gold:#C9A84C; --gold-l:#E8C97A; --gold-d:rgba(201,168,76,.08);
  --dark:#07070D; --dark2:#0D0D16; --dark3:#13131E; --dark4:#1A1A28;
  --surf:rgba(255,255,255,.03);
  --border:rgba(201,168,76,.14); --border2:rgba(201,168,76,.07);
  --text:#EAEAF4; --muted:#5A5A78; --soft:#9898B8; --white:#FFFFFF;
  --green:#10B981; --green-d:rgba(16,185,129,.1); --green-b:rgba(16,185,129,.25);
  --amber:#F59E0B; --amber-d:rgba(245,158,11,.1); --amber-b:rgba(245,158,11,.25);
  --red:#F87171;
  --f-display:'Instrument Serif',Georgia,serif;
  --f-head:'Syne',sans-serif;
  --f-mono:'JetBrains Mono',monospace;
  --f-body:'Syne',sans-serif;
  --r:16px; --r-lg:24px;
  --nav-h:64px;
}

*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
html{scroll-behavior:smooth;-webkit-text-size-adjust:100%;}
body{font-family:var(--f-body);background:var(--dark);color:var(--text);
  min-height:100dvh;overflow-x:hidden;-webkit-font-smoothing:antialiased;}

/* ── NOISE TEXTURE ── */
body::after{
  content:'';position:fixed;inset:0;z-index:0;pointer-events:none;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
  opacity:.6;
}

/* ── AMBIENT ORBS ── */
.orb{position:fixed;border-radius:50%;pointer-events:none;z-index:0;filter:blur(90px);}
.orb-1{width:500px;height:500px;top:-150px;left:-100px;background:radial-gradient(circle,rgba(201,168,76,.08),transparent 70%);}
.orb-2{width:400px;height:400px;bottom:-100px;right:-80px;background:radial-gradient(circle,rgba(16,185,129,.06),transparent 70%);}

/* ── PROGRESS LINE ── */
.progress-line{
  position:fixed;top:0;left:0;right:0;height:2px;z-index:1100;
  background:linear-gradient(90deg,transparent,var(--gold),var(--gold-l),var(--gold),transparent);
  transform-origin:left;animation:lineIn .8s cubic-bezier(.22,1,.36,1) forwards;
}
@keyframes lineIn{from{transform:scaleX(0)}to{transform:scaleX(1)}}

/* ── NAV ── */
.nav{
  position:fixed;top:2px;left:0;right:0;z-index:1000;height:var(--nav-h);
  display:flex;align-items:center;justify-content:space-between;
  padding:0 clamp(1rem,5vw,5%);
  background:rgba(7,7,13,.88);backdrop-filter:blur(24px) saturate(180%);
  border-bottom:1px solid var(--border2);
}
.nav-logo{font-family:var(--f-display);font-size:clamp(1.1rem,2.5vw,1.5rem);
  color:var(--gold);cursor:pointer;letter-spacing:.01em;font-style:italic;}
.nav-logo span{color:var(--white);font-style:normal;}
.nav-right{display:flex;align-items:center;gap:.75rem;}
.nav-ref{font-family:var(--f-mono);font-size:.68rem;color:var(--muted);
  letter-spacing:1px;white-space:nowrap;}
.nav-ref strong{color:var(--gold);}
.nav-badge{
  font-family:var(--f-mono);font-size:.6rem;letter-spacing:1.5px;text-transform:uppercase;
  padding:.25rem .65rem;border-radius:100px;
}
.nav-badge.success {background:var(--green-d);color:var(--green);border:1px solid var(--green-b);}
.nav-badge.paylater{background:var(--amber-d);color:var(--amber);border:1px solid var(--amber-b);}

/* ── PAGE WRAPPER ── */
.page{position:relative;z-index:1;padding-top:calc(var(--nav-h) + 2px);min-height:100dvh;}

/* ── ANIMATIONS ── */
@keyframes fadeUp{
  from{opacity:0;transform:translateY(28px)}
  to  {opacity:1;transform:translateY(0)}
}
@keyframes scaleIn{
  from{opacity:0;transform:scale(.85)}
  to  {opacity:1;transform:scale(1)}
}
@keyframes floatY{
  0%,100%{transform:translateY(0)}
  50%    {transform:translateY(-8px)}
}
@keyframes ringPulse{
  0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,.3)}
  50%    {box-shadow:0 0 0 22px rgba(16,185,129,0)}
}
@keyframes ringPulseA{
  0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,.3)}
  50%    {box-shadow:0 0 0 22px rgba(245,158,11,0)}
}
@keyframes shimmer{
  0%  {background-position:-800px 0}
  100%{background-position:800px 0}
}
@keyframes tickAppear{
  0%  {transform:scale(0) rotate(-20deg);opacity:0}
  60% {transform:scale(1.25) rotate(5deg);opacity:1}
  100%{transform:scale(1) rotate(0deg);opacity:1}
}

.anim{opacity:0;}
.anim.in{animation:fadeUp .65s cubic-bezier(.22,1,.36,1) forwards;}

/* ── HERO ── */
.hero{
  padding:clamp(2.5rem,6vw,4.5rem) clamp(1rem,5vw,5%) clamp(1.5rem,4vw,3rem);
  text-align:center;
  opacity:0;animation:fadeUp .7s .1s cubic-bezier(.22,1,.36,1) forwards;
}

.hero-ring-wrap{
  width:110px;height:110px;margin:0 auto 1.75rem;
  position:relative;display:flex;align-items:center;justify-content:center;
  opacity:0;animation:scaleIn .6s .05s cubic-bezier(.34,1.56,.64,1) forwards;
}
.hero-ring{
  position:absolute;inset:0;border-radius:50%;
}
.hero-ring.success{
  background:var(--green-d);border:1.5px solid var(--green-b);
  animation:ringPulse 3s 1s ease-in-out infinite;
}
.hero-ring.paylater{
  background:var(--amber-d);border:1.5px solid var(--amber-b);
  animation:ringPulseA 3s 1s ease-in-out infinite;
}
.hero-ring-inner{
  width:70px;height:70px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  position:relative;z-index:1;
}
.hero-ring-inner.success {background:linear-gradient(135deg,rgba(16,185,129,.2),rgba(16,185,129,.05));}
.hero-ring-inner.paylater{background:linear-gradient(135deg,rgba(245,158,11,.2),rgba(245,158,11,.05));}
.hero-icon{
  font-size:2rem;line-height:1;
  animation:tickAppear .5s .45s cubic-bezier(.34,1.56,.64,1) both;
}

.hero-tag{
  display:inline-flex;align-items:center;gap:.4rem;
  font-family:var(--f-mono);font-size:.62rem;letter-spacing:3px;text-transform:uppercase;
  margin-bottom:.85rem;
}
.hero-tag.success {color:var(--green);}
.hero-tag.paylater{color:var(--amber);}
.hero-tag::before,.hero-tag::after{content:'';width:28px;height:1px;}
.hero-tag.success::before,.hero-tag.success::after{background:var(--green);}
.hero-tag.paylater::before,.hero-tag.paylater::after{background:var(--amber);}

.hero h1{
  font-family:var(--f-display);
  font-size:clamp(2rem,5.5vw,3.6rem);
  font-weight:400;font-style:italic;
  color:var(--white);line-height:1.12;
  margin-bottom:.9rem;letter-spacing:-.01em;
}
.hero h1 em{font-style:normal;color:var(--gold);}
.hero-sub{
  font-size:clamp(.82rem,2vw,.95rem);color:var(--soft);
  max-width:480px;margin:0 auto 1.75rem;line-height:1.7;
}

/* Booking ID pill */
.bid-pill{
  display:inline-flex;align-items:stretch;
  background:var(--dark3);border:1px solid var(--border);
  border-radius:12px;overflow:hidden;
  animation:fadeUp .5s .6s both;
}
.bid-left{padding:.65rem 1.1rem;}
.bid-eyebrow{font-family:var(--f-mono);font-size:.55rem;letter-spacing:2px;
  text-transform:uppercase;color:var(--muted);margin-bottom:.2rem;}
.bid-num{font-family:var(--f-mono);font-size:.95rem;color:var(--gold);font-weight:500;letter-spacing:.5px;}
.bid-copy{
  background:rgba(201,168,76,.08);border:none;border-left:1px solid var(--border);
  padding:.65rem .9rem;cursor:pointer;color:var(--muted);font-size:.9rem;
  transition:all .2s;display:flex;align-items:center;justify-content:center;
}
.bid-copy:hover{background:rgba(201,168,76,.16);color:var(--gold);}
.bid-copy.copied{color:var(--green);background:var(--green-d);}

/* ── JOURNEY TRACK ── */
.journey-track{
  padding:1.25rem clamp(1rem,5vw,5%);
  opacity:0;animation:fadeUp .6s .3s cubic-bezier(.22,1,.36,1) forwards;
  overflow-x:auto;-webkit-overflow-scrolling:touch;
  scrollbar-width:none;
}
.journey-track::-webkit-scrollbar{display:none;}
.track-inner{
  display:flex;align-items:flex-start;justify-content:center;
  min-width:520px;max-width:800px;margin:0 auto;
  position:relative;
}
.track-line{
  position:absolute;top:19px;left:calc(10%);right:calc(10%);
  height:1px;background:linear-gradient(90deg,var(--green-b),var(--border),var(--border2),var(--border2));
  z-index:0;
}
.track-step{
  display:flex;flex-direction:column;align-items:center;gap:.5rem;
  flex:1;position:relative;z-index:1;padding:0 .25rem;
}
.track-dot{
  width:38px;height:38px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;font-size:.85rem;
  transition:transform .2s;
}
.track-dot:hover{transform:scale(1.1);}
.track-dot.done  {background:var(--green-d);border:1.5px solid var(--green);color:var(--green);}
.track-dot.active{background:var(--gold-d);border:1.5px solid var(--gold);color:var(--gold);}
.track-dot.wait  {background:var(--dark4);border:1.5px solid var(--border);color:var(--muted);}
.track-label{
  font-family:var(--f-mono);font-size:.58rem;letter-spacing:.5px;text-transform:uppercase;
  text-align:center;line-height:1.4;
}
.track-label.done  {color:var(--green);}
.track-label.active{color:var(--gold);}
.track-label.wait  {color:var(--muted);}

/* ── MAIN GRID ── */
.main-grid{
  display:grid;grid-template-columns:1fr 360px;
  gap:0;
  padding:1.5rem clamp(1rem,4vw,5%) 5rem;
  max-width:1280px;margin:0 auto;
  align-items:start;
}

/* ── CARDS ── */
.c-card{
  background:var(--surf);
  border:1px solid var(--border2);
  border-radius:var(--r-lg);
  padding:clamp(1.1rem,2.5vw,1.6rem);
  margin-bottom:1.1rem;
  opacity:0;
  backdrop-filter:blur(8px);
  position:relative;overflow:hidden;
}
.c-card::before{
  content:'';position:absolute;top:0;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,var(--border),transparent);
}
.c-card.in{animation:fadeUp .6s cubic-bezier(.22,1,.36,1) forwards;}
.c-card:hover{border-color:rgba(201,168,76,.2);transition:border-color .3s;}

.c-head{
  font-family:var(--f-mono);font-size:.6rem;letter-spacing:3px;text-transform:uppercase;
  color:var(--gold);margin-bottom:1.25rem;
  display:flex;align-items:center;gap:.7rem;
}
.c-head::after{content:'';flex:1;height:1px;background:linear-gradient(90deg,var(--border),transparent);}

/* ── VEHICLE HERO ── */
.vehicle-hero{
  display:flex;align-items:center;gap:1.1rem;
  padding:1rem;background:var(--dark4);
  border-radius:14px;border:1px solid var(--border2);
  position:relative;overflow:hidden;
}
.vehicle-hero::after{
  content:'';position:absolute;inset:0;
  background:linear-gradient(135deg,rgba(201,168,76,.04),transparent 60%);
  pointer-events:none;
}
.vh-glyph{
  width:70px;height:70px;border-radius:12px;
  background:linear-gradient(135deg,rgba(201,168,76,.12),rgba(201,168,76,.04));
  border:1px solid rgba(201,168,76,.2);
  display:flex;align-items:center;justify-content:center;
  font-size:2rem;flex-shrink:0;
  animation:floatY 4s ease-in-out infinite;
}
.vh-cat{font-family:var(--f-mono);font-size:.58rem;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:.3rem;}
.vh-name{font-family:var(--f-display);font-size:clamp(1.3rem,3vw,1.7rem);color:var(--white);font-style:italic;margin-bottom:.2rem;}
.vh-sub{font-size:.78rem;color:var(--muted);}
.vh-tags{display:flex;gap:.4rem;flex-wrap:wrap;margin-top:.6rem;}
.vh-tag{
  font-family:var(--f-mono);font-size:.65rem;color:var(--soft);
  background:rgba(255,255,255,.04);padding:.18rem .55rem;
  border-radius:6px;border:1px solid var(--border2);
}

/* ── DETAIL GRID ── */
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;}
.d-item{
  padding:.7rem 0;
  border-bottom:1px solid var(--border2);
  position:relative;
}
.d-item:nth-child(odd){padding-right:1.5rem;}
.d-item:nth-child(even){padding-left:1.5rem;border-left:1px solid var(--border2);}
.d-item:nth-last-child(-n+2){border-bottom:none;}
.d-label{font-family:var(--f-mono);font-size:.6rem;letter-spacing:1.2px;text-transform:uppercase;
  color:var(--muted);margin-bottom:.3rem;}
.d-val{font-size:.88rem;color:var(--text);font-weight:500;word-break:break-word;line-height:1.4;}

/* ── EXTRA CHIPS ── */
.extras-list{display:flex;flex-direction:column;gap:.6rem;}
.extra-chip{
  display:flex;align-items:center;gap:.85rem;
  padding:.7rem 1rem;border-radius:12px;
  background:var(--dark4);border:1px solid var(--border2);
  transition:border-color .2s,transform .2s;
  cursor:default;
}
.extra-chip:hover{border-color:rgba(201,168,76,.18);transform:translateX(3px);}
.ec-icon{font-size:1.1rem;width:26px;text-align:center;flex-shrink:0;}
.ec-info{flex:1;min-width:0;}
.ec-name{font-size:.84rem;color:var(--text);font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ec-meta{font-family:var(--f-mono);font-size:.68rem;color:var(--muted);margin-top:.15rem;}
.ec-right{display:flex;align-items:center;gap:.5rem;flex-shrink:0;}
.ec-price{font-family:var(--f-mono);font-size:.82rem;font-weight:500;}
.ec-price.free{color:var(--green);}
.ec-price.paid{color:var(--gold);}
.ec-price.ins {color:var(--gold);}
.ec-badge{
  font-family:var(--f-mono);font-size:.55rem;letter-spacing:1px;
  padding:.18rem .5rem;border-radius:5px;text-transform:uppercase;font-weight:700;
}
.ec-badge.free{background:var(--green-d);color:var(--green);border:1px solid var(--green-b);}
.ec-badge.paid{background:var(--gold-d);color:var(--gold);border:1px solid rgba(201,168,76,.25);}
.ec-badge.ins {background:rgba(99,102,241,.1);color:#a5b4fc;border:1px solid rgba(99,102,241,.2);}

.no-extras{
  padding:1.4rem;text-align:center;border-radius:12px;
  background:var(--dark4);border:1px dashed var(--border2);
  font-size:.82rem;color:var(--muted);letter-spacing:.3px;
}

/* ── RIGHT COLUMN ── */
.right-col{
  padding-left:1.5rem;
  display:flex;flex-direction:column;gap:1rem;
  opacity:0;animation:fadeUp .65s .55s cubic-bezier(.22,1,.36,1) forwards;
}

/* Price card */
.price-card{
  background:var(--dark3);border:1px solid var(--border);
  border-radius:var(--r-lg);padding:1.4rem;
  position:sticky;top:calc(var(--nav-h) + 1.5rem);
  backdrop-filter:blur(12px);
}
.price-card::before{
  content:'';position:absolute;inset:0;border-radius:var(--r-lg);pointer-events:none;
  background:linear-gradient(135deg,rgba(201,168,76,.04),transparent 50%);
}

.pr-row{display:flex;justify-content:space-between;align-items:center;padding:.38rem 0;gap:.5rem;}
.pr-label{font-size:.79rem;color:var(--muted);}
.pr-val{font-family:var(--f-mono);font-size:.82rem;color:var(--text);}
.pr-sep{height:1px;background:var(--border2);margin:.6rem 0;}
.pr-total-row{display:flex;justify-content:space-between;align-items:baseline;padding:.35rem 0 .1rem;}
.pr-total-label{font-family:var(--f-head);font-size:.9rem;color:var(--soft);font-weight:600;}
.pr-total-val{font-family:var(--f-display);font-size:2.1rem;color:var(--white);font-style:italic;}

/* Payment status */
.pay-box{
  border-radius:var(--r);padding:1.25rem;text-align:center;
  position:relative;overflow:hidden;
}
.pay-box.success{background:var(--green-d);border:1px solid var(--green-b);}
.pay-box.paylater{background:var(--amber-d);border:1px solid var(--amber-b);}
.pay-box::before{
  content:'';position:absolute;top:-30px;right:-30px;
  width:120px;height:120px;border-radius:50%;opacity:.06;
}
.pay-box.success::before{background:var(--green);}
.pay-box.paylater::before{background:var(--amber);}
.pb-icon{font-size:1.6rem;margin-bottom:.35rem;display:block;}
.pb-eyebrow{font-family:var(--f-mono);font-size:.58rem;letter-spacing:2.5px;text-transform:uppercase;margin-bottom:.3rem;}
.pb-eyebrow.success{color:var(--green);}
.pb-eyebrow.paylater{color:var(--amber);}
.pb-amount{font-family:var(--f-display);font-size:2.2rem;font-style:italic;font-weight:400;margin-bottom:.3rem;}
.pb-amount.success{color:var(--green);}
.pb-amount.paylater{color:var(--amber);}
.pb-note{font-size:.74rem;color:var(--muted);line-height:1.6;max-width:260px;margin:0 auto;}

/* Actions */
.action-stack{display:flex;flex-direction:column;gap:.55rem;}
.btn-gold{
  width:100%;background:linear-gradient(135deg,var(--gold),var(--gold-l));
  color:#07070D;font-family:var(--f-head);font-weight:700;font-size:.88rem;
  padding:.88rem;border:none;border-radius:12px;cursor:pointer;
  transition:all .25s;display:flex;align-items:center;justify-content:center;gap:.5rem;
  letter-spacing:.3px;touch-action:manipulation;
}
.btn-gold:hover{transform:translateY(-2px);box-shadow:0 10px 30px rgba(201,168,76,.25);}
.btn-ghost{
  width:100%;background:transparent;color:var(--soft);
  font-family:var(--f-head);font-weight:500;font-size:.84rem;
  padding:.78rem;border:1px solid var(--border);border-radius:12px;cursor:pointer;
  transition:all .2s;touch-action:manipulation;
}
.btn-ghost:hover{color:var(--text);border-color:var(--border);background:rgba(255,255,255,.03);}

/* Support box */
.support-box{
  background:var(--dark4);border:1px solid var(--border2);
  border-radius:14px;padding:1rem 1.15rem;
  font-size:.76rem;color:var(--muted);line-height:1.7;text-align:center;
}
.support-box strong{color:var(--soft);}
.support-link{color:var(--gold);text-decoration:none;}
.support-link:hover{text-decoration:underline;}

/* Skeleton */
.sk{background:linear-gradient(90deg,var(--dark3) 25%,var(--dark4) 50%,var(--dark3) 75%);
  background-size:800px 100%;animation:shimmer 1.4s infinite;border-radius:8px;}

/* Error */
.err-wrap{text-align:center;padding:6rem 2rem;position:relative;z-index:1;}
.err-wrap h2{font-family:var(--f-display);color:var(--red);font-size:2.4rem;font-style:italic;margin-bottom:.9rem;}
.err-wrap p{color:var(--muted);margin-bottom:2rem;font-size:.9rem;}

/* ═══════════ RESPONSIVE ═══════════ */

@media(max-width:1024px){
  .main-grid{grid-template-columns:1fr 320px;}
}

@media(max-width:900px){
  .main-grid{
    display:flex;flex-direction:column;
    padding:1.25rem clamp(.9rem,3vw,1.5rem) 5rem;
  }
  .right-col{
    padding-left:0;
    position:relative;
  }
  .price-card{position:relative;top:0;}
  .track-inner{min-width:380px;}
}

@media(max-width:640px){
  .nav-ref{display:none;}
  .detail-grid{grid-template-columns:1fr;}
  .d-item{padding:.6rem 0 !important;border-left:none !important;padding-left:0 !important;}
  .d-item:nth-last-child(-n+2){border-bottom:1px solid var(--border2);}
  .d-item:last-child{border-bottom:none;}
  .vehicle-hero{flex-direction:column;text-align:center;}
  .vh-tags{justify-content:center;}
  .track-inner{min-width:320px;}
  .ec-name{
    max-width: 21ch;
  }
}

@media(max-width:480px){
  :root{--nav-h:56px;}
  .hero h1{font-size:1.85rem;}
  .hero-sub{font-size:.82rem;}
  .pr-total-val{font-size:1.7rem;}
  .pb-amount{font-size:1.8rem;}
  .extra-chip{padding:.6rem .75rem;}
  .ec-badge{display:none;}
  .journey-track{padding:.9rem clamp(.75rem,3vw,1.5rem);}
}

@media(max-width:360px){
  .bid-pill{flex-direction:column;}
  .bid-copy{border-left:none;border-top:1px solid var(--border);}
  .track-inner{min-width:280px;}
}

/* Print */
@media print{
  .nav,.journey-track,.action-stack,.orb,.progress-line{display:none!important;}
  body{background:#fff;color:#000;}
  .c-card,.price-card,.pay-box{border:1px solid #ddd;background:#fff;}
  .page{padding-top:0;}
}
`;

// ─── SKELETON ────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ padding:"2rem clamp(1rem,5vw,5%)", maxWidth:1200, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:"2.5rem" }}>
        {[80,200,140,120].map((w,i) => (
          <div key={i} className="sk" style={{ height:i===0?80:16, width:i===0?80:w, margin:"0 auto 12px", borderRadius:i===0?"50%":8 }} />
        ))}
      </div>
      {[1,2,3].map(i => (
        <div key={i} style={{ background:"var(--dark3)", border:"1px solid var(--border2)", borderRadius:24, padding:"1.6rem", marginBottom:"1rem" }}>
          {[70,50,40].map((w,j) => (
            <div key={j} className="sk" style={{ height:14, width:`${w}%`, marginBottom:10 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── EXTRA CHIP ──────────────────────────────────────────────
function ExtraChip({ icon, name, meta, price, priceClass, badgeClass, badgeLabel }) {
  return (
    <div className="extra-chip">
      <span className="ec-icon">{icon}</span>
      <div className="ec-info">
        <div className="ec-name">{name}</div>
        {meta && <div className="ec-meta">{meta}</div>}
      </div>
      <div className="ec-right">
        <span className={`ec-price ${priceClass}`}>{price}</span>
        <span className={`ec-badge ${badgeClass}`}>{badgeLabel}</span>
      </div>
    </div>
  );
}

// ─── USE INTERSECTION ────────────────────────────────────────
function useReveal(delay = 0) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => el.classList.add("in"), delay);
        obs.disconnect();
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

// ─── ANIMATED CARD ───────────────────────────────────────────
function Card({ children, delay = 0, className = "" }) {
  const ref = useReveal(delay);
  return <div ref={ref} className={`c-card ${className}`}>{children}</div>;
}

// ─── MAIN ────────────────────────────────────────────────────
export default function BookingConfirmation() {
  const navigate = useNavigate();
  const { id }   = useParams();

  const [booking,    setBooking]  = useState(null);
  const [freeExtras, setFree]     = useState([]);
  const [paidExtras, setPaid]     = useState([]);
  // const [insurance,  setIns]      = useState(null);
  const [insurance,  setIns]      = useState([]);
  const [loading,    setLoading]  = useState(true);
  const [error,      setError]    = useState(null);
  const [copied,     setCopied]   = useState(false);
  const [showConf,   setShowConf] = useState(false);

  useEffect(() => {
    if (!id || isNaN(id)) { navigate("/"); return; }
    fetch(`${API_BASE}/booking/${id}`)
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setBooking(res.booking);
          setFree(res.freeExtras  || []);
          setPaid(res.paidExtras  || []);
          // setIns(res.insurance    || null);
          console.log("✅ Full API response:", res);
          console.log("✅ insurance:", res.insurance);
          setIns(
            Array.isArray(res.insurance) ? res.insurance
            : res.insurance ? [res.insurance]
            : []
          );
          setTimeout(() => setShowConf(true), 400);
        } else {
          setError(res.message || "Booking not found.");
        }
      })
      .catch(() => setError("Could not connect to server."))
      .finally(() => setLoading(false));
  }, [id]);

  const copyId = () =>
    navigator.clipboard.writeText(String(id)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });

  // ── Loading ──────────────────────────────────────────────
  if (loading) return (
    <>
      <style>{css}</style>
      <div className="orb orb-1" /><div className="orb orb-2" />
      <div className="progress-line" />
      <nav className="nav">
        <div className="nav-logo">Orisnz<span>-Fleet</span></div>
      </nav>
      <div className="page"><Skeleton /></div>
    </>
  );

  // ── Error ────────────────────────────────────────────────
  if (error || !booking) return (
    <>
      <style>{css}</style>
      <div className="orb orb-1" /><div className="orb orb-2" />
      <div className="progress-line" />
      <nav className="nav">
        <div className="nav-logo" onClick={() => navigate("/")}>Orisnz<span>-Fleet</span></div>
      </nav>
      <div className="page">
        <div className="err-wrap">
          <h2>Booking Not Found</h2>
          <p>{error || `We couldn't locate booking #${id}.`}</p>
          <button className="btn-gold" style={{ maxWidth:200, margin:"0 auto" }} onClick={() => navigate("/")}>
            ← Go Home
          </button>
        </div>
      </div>
    </>
  );

  // ── Derived values ───────────────────────────────────────
  const b          = booking;
  const payLater   = n(b.pay_status) === 1;
  const days       = n(b.online_diffdate) || 1;
  const basePrice  = n(b.online_onlineprice);
  const baseTotal  = basePrice * days;
  const nightChgs  = n(b.online_picknightcharge) + n(b.online_dropoffnightcharge);
  const taxRate    = n(b.online_localtax) / 100 || 0.18;
  const advAmount  = n(b.prevadvpaid);
  // const insTotal   = insurance ? (n(insurance.ins_amount) > 0 ? n(insurance.ins_amount) : n(insurance.ins_chargeperday) * days) : 0;
  const insTotal = (insurance || []).reduce((sum, ins) =>
  sum + (n(ins.ins_amount) > 0 ? n(ins.ins_amount) : n(ins.ins_chargeperday) * days), 0
);
  const extrasTotal= paidExtras.reduce((s, e) => s + n(e.opt_dayrate) * days, 0);
  const subtotal   = baseTotal + nightChgs + extrasTotal + insTotal;
  const taxAmount  = Math.round(subtotal * taxRate);
  const grandTotal = Math.round(subtotal + taxAmount);
  const firstName  = b.online_firstname || "";
  const fullName   = `${firstName} ${b.online_lastname || ""}`.trim();
  // const hasExtras  = freeExtras.length > 0 || paidExtras.length > 0 || insurance;
  const hasExtras  = freeExtras.length > 0 || paidExtras.length > 0 || insurance.length > 0;
  const mode       = payLater ? "paylater" : "success";

  const tlSteps = [
    { icon:"✓",  label:"Details\nEntered",   state:"done"   },
    { icon:"✓",  label:"Booking\nConfirmed", state:"done"   },
    { icon: payLater ? "🕐" : "✓",
      label: payLater ? "Pay at\nPickup" : "Advance\nPaid",
      state: payLater ? "active" : "done" },
    { icon:"🚗", label:"Vehicle\nReady",     state:"wait"   },
    { icon:"🏁", label:"Journey\nBegins",    state:"wait"   },
  ];

  return (
    <>
      <style>{css}</style>
      <Confetti active={showConf && !payLater} />
      <div className="orb orb-1" /><div className="orb orb-2" />
      <div className="progress-line" />

      <nav className="nav">
        <div className="nav-logo" onClick={() => navigate("/")}>Orisnz<span>-Fleet</span></div>
        <div className="nav-right">
          <div className="nav-ref">Ref &nbsp;<strong>#{id}</strong></div>
          <div className={`nav-badge ${mode}`}>
            {payLater ? "Pay Later" : "Confirmed"}
          </div>
        </div>
      </nav>

      <div className="page">

        {/* ── HERO ── */}
        <div className="hero">
          <div className="hero-ring-wrap">
            <div className={`hero-ring ${mode}`} />
            <div className={`hero-ring-inner ${mode}`}>
              <span className="hero-icon">{payLater ? "🕐" : "✅"}</span>
            </div>
          </div>

          <div className={`hero-tag ${mode}`}>
            {payLater ? "Reservation Confirmed" : "Booking Confirmed"}
          </div>

          <h1>
            {payLater ? (
              <>Your car is <em>reserved</em>,<br />pay when you arrive.</>
            ) : (
              <>You&apos;re all set,<br /><em>{firstName || "traveller"}</em>!</>
            )}
          </h1>

          <p className="hero-sub">
            {payLater
              ? "Your reservation is locked in. No payment needed now — settle the full amount at pickup. A confirmation has been sent to your email."
              : "Booking confirmed and payment received. A full itinerary has been sent to your email. See you on the road!"}
          </p>

          <div className="bid-pill">
            <div className="bid-left">
              <div className="bid-eyebrow">Booking ID</div>
              <div className="bid-num">#{id}</div>
            </div>
            <button className={`bid-copy ${copied ? "copied" : ""}`} onClick={copyId}
              title="Copy booking ID">
              {copied ? "✓" : "⎘"}
            </button>
          </div>
        </div>

        {/* ── JOURNEY TRACK ── */}
        <div className="journey-track">
          <div className="track-inner">
            <div className="track-line" />
            {tlSteps.map((s, i) => (
              <div key={i} className="track-step">
                <div className={`track-dot ${s.state}`}>{s.icon}</div>
                <div className={`track-label ${s.state}`}>
                  {s.label.split("\n").map((ln, j) => <span key={j} style={{ display:"block" }}>{ln}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MAIN GRID ── */}
        <div className="main-grid">

          {/* LEFT */}
          <div>

            {/* Vehicle */}
            <Card delay={0}>
              <div className="c-head">Your Vehicle</div>
              <div className="vehicle-hero">
                <div className="vh-glyph">🚘</div>
                <div>
                  <div className="vh-cat">{b.online_groupid || "Vehicle"}</div>
                  <div className="vh-name">{b.online_groupid || `Car #${b.online_carid}`}</div>
                  <div className="vh-sub">Booking #{b.bookingid}</div>
                  <div className="vh-tags">
                    <span className="vh-tag">{days} day{days !== 1 ? "s" : ""}</span>
                    <span className="vh-tag">{fmtPrice(basePrice)}/day</span>
                    <span className="vh-tag">Base {fmtPrice(baseTotal)}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Trip */}
            <Card delay={80}>
              <div className="c-head">Trip Details</div>
              <div className="detail-grid">
                {[
                  ["📍 Pickup Location", b.online_pickcity || "—"],
                  ["🏁 Drop-off Location", b.online_returncity || b.online_pickcity || "—"],
                  ["📅 Pickup", `${fmtDate(b.online_pickdate)} · ${b.online_picktime || "—"}`],
                  ["📅 Return",  `${fmtDate(b.online_returndate)} · ${b.online_returntime || "—"}`],
                  ["⏱ Duration", `${days} day${days !== 1 ? "s" : ""}`],
                  b.online_driverage ? ["👤 Driver Age", b.online_driverage] : null,
                ].filter(Boolean).map(([label, val], i) => (
                  <div key={i} className="d-item">
                    <div className="d-label">{label}</div>
                    <div className="d-val">{val}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Customer */}
            <Card delay={160}>
              <div className="c-head">Customer Details</div>
              <div className="detail-grid">
                {[
                  ["👤 Full Name",  `${b.online_title || "Mr"} ${fullName}`],
                  ["✉ Email",       b.online_emailid  || "—"],
                  ["📱 Mobile",     b.online_mobileno || "—"],
                  ["🌏 Country",    b.online_country  || "—"],
                  b.dl_number    ? ["🪪 Driving Licence", b.dl_number] : null,
                  b.dl_expirydate? ["📆 DL Expiry",       fmtDate(b.dl_expirydate)] : null,
                  b.online_address ? ["🏠 Address",        b.online_address] : null,
                  b.online_comments? ["💬 Comments",       b.online_comments] : null,
                ].filter(Boolean).map(([label, val], i) => (
                  <div key={i} className="d-item">
                    <div className="d-label">{label}</div>
                    <div className="d-val">{val}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Extras */}
            <Card delay={240}>
              <div className="c-head">Extras &amp; Insurance</div>
              {!hasExtras ? (
                <div className="no-extras">No extras or insurance selected for this booking.</div>
              ) : (
                <div className="extras-list">
                  {freeExtras.map((e, i) => (
                    <ExtraChip key={`f${i}`} icon="🎁"
                      name={e.optitem_name || `Extra #${e.online_extraitemids}`}
                      meta={`Qty: ${n(e.online_extraitemqty)}`}
                      price="Free" priceClass="free" badgeClass="free" badgeLabel="Included" />
                  ))}
                  {paidExtras.map((e, i) => {
                    const lt = n(e.opt_dayrate) * days;
                    const pd = (e.rate_type || "").toLowerCase().includes("day");
                    return (
                      <ExtraChip key={`p${i}`} icon="➕"
                        name={e.optitem_name || `Extra #${e.online_extraitemids}`}
                        meta={pd ? `${fmtPrice(e.opt_dayrate)}/day × ${days}d` : "Flat rate"}
                        price={fmtPrice(lt)} priceClass="paid" badgeClass="paid" badgeLabel="Add-on" />
                    );
                  })}
                  {/* {insurance && (
                    <ExtraChip icon="🛡️"
                      name={insurance.ins_item || `Insurance #${insurance.ins_id}`}
                      meta={n(insurance.ins_amount) > 0
                        ? `Total for ${days} day${days !== 1 ? "s" : ""}`
                        : insurance.ins_chargeperday
                          ? `${fmtPrice(insurance.ins_chargeperday)}/day × ${days}d`
                          : null}
                      price={fmtPrice(insTotal)} priceClass="ins" badgeClass="ins" badgeLabel="Insurance" />
                  )} */}
                  {(insurance || []).map((ins, i) => {
                  const insAmt = n(ins.ins_amount) > 0
                  ? n(ins.ins_amount)
                  : n(ins.ins_chargeperday) * days;
                  return (
                  <ExtraChip key={`ins${i}`} icon="🛡️"
                  name={ins.ins_item || `Insurance #${ins.ins_id}`}
                  meta={n(ins.ins_amount) > 0
                  ? `Total for ${days} day${days !== 1 ? "s" : ""}`
                  : ins.ins_chargeperday
                  ? `${fmtPrice(ins.ins_chargeperday)}/day × ${days}d`
                  : null}
                  price={fmtPrice(insAmt)} priceClass="ins" badgeClass="ins" badgeLabel="Insurance" />
                  );
                  })}
                </div>
              )}
            </Card>

          </div>

          {/* RIGHT */}
          <div className="right-col">

            {/* Price Breakdown */}
            <div className="price-card">
              <div className="c-head">Price Breakdown</div>

              {[
                [`${fmtPrice(basePrice)}/day × ${days}d`, baseTotal],
                nightChgs > 0 ? ["🌙 Night charges", nightChgs] : null,
                extrasTotal > 0 ? ["Add-ons total", extrasTotal] : null,
                // insTotal > 0    ? [`🛡️ ${insurance?.ins_item || "Insurance"}`, insTotal] : null,
                ...(insurance || []).map(ins => {
                  const insAmt = n(ins.ins_amount) > 0
                    ? n(ins.ins_amount)
                    : n(ins.ins_chargeperday) * days;
                  return insAmt > 0 ? [`🛡️ ${ins.ins_item || "Insurance"}`, insAmt] : null;
                }).filter(Boolean),
                taxAmount > 0   ? [`Tax (${n(b.online_localtax)}%)`, taxAmount] : null,
              ].filter(Boolean).map(([label, val], i) => (
                <div key={i} className="pr-row">
                  <span className="pr-label">{label}</span>
                  <span className="pr-val">{fmtPrice(val)}</span>
                </div>
              ))}

              <div className="pr-sep" />
              <div className="pr-total-row">
                <span className="pr-total-label">Grand Total</span>
                <span className="pr-total-val">{fmtPrice(grandTotal)}</span>
              </div>
            </div>

            {/* Payment Status */}
            <div className={`pay-box ${mode}`}>
              <span className="pb-icon">{payLater ? "🕐" : "✅"}</span>
              <div className={`pb-eyebrow ${mode}`}>
                {payLater ? "Due at Pickup" : "Advance Paid"}
              </div>
              <div className={`pb-amount ${mode}`}>
                {payLater ? fmtPrice(grandTotal) : fmtPrice(advAmount)}
              </div>
              <div className="pb-note">
                {payLater
                  ? "Full payment due when you collect the vehicle. Please carry a valid photo ID."
                  : `Balance of ${fmtPrice(grandTotal - advAmount)} due at pickup.`}
              </div>
            </div>

            {/* Actions */}
            <div className="action-stack">
              <button className="btn-gold" onClick={() => window.print()}>
                🖨 Print / Save Confirmation
              </button>
              <button className="btn-ghost" onClick={() => navigate("/booking-step2")}>
                + New Booking
              </button>
              <button className="btn-ghost" onClick={() => navigate("/")}>
                ← Back to Home
              </button>
            </div>

            {/* Support */}
            <div className="support-box">
              Need help? Email{" "}
              <a className="support-link" href="mailto:support@orisnzfleet.com">
                support@orisnzfleet.com
              </a>{" "}
              or call <strong>+91 98765 43210</strong> quoting <strong>#{id}</strong>.
            </div>

          </div>
        </div>
      </div>
    </>
  );
}